import { fetchQuestionnaireElement, fetchQuestionnaireResponse } from '@api/taptotip/questionnaire';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { CARD } from './Editor';
import { ResponseDrawer } from './Responses';
import { formatDeparture, formatSubmitted, ratingWord } from './responses';
import Stars from './Stars';

/**
 * Andy, 2026-10-02: one hotel or experience on its own page. Its average and
 * how the stars split, then every guest's rating of it; each opens that
 * guest's whole questionnaire.
 */

const KIND = { hotel: 'Hotel', experience: 'Experience' };
const NOT_USED = { hotel: "Didn't stay here", experience: "Didn't take part" };

const ElementDetail = ({ element, onBack }) => {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('loading');
  const [star, setStar] = useState(null);
  const [open, setOpen] = useState(null);
  const [opening, setOpening] = useState('');

  const load = useCallback(() => {
    setStatus('loading');
    const { kind, name, place, tourId, from } = element;
    return fetchQuestionnaireElement({
      kind,
      name,
      ...(place ? { place } : {}),
      ...(tourId ? { tourId } : {}),
      ...(from ? { from } : {})
    })
      .then(result => {
        setData(result);
        setStatus('ready');
      })
      .catch(() => setStatus('error'));
  }, [element]);

  useEffect(() => {
    load();
  }, [load]);

  const openResponse = id => {
    setOpening(id);
    fetchQuestionnaireResponse(id)
      .then(response => setOpen(response))
      .catch(() => setOpen(null))
      .finally(() => setOpening(''));
  };

  const back = (
    <button
      type="button"
      onClick={onBack}
      className="self-start text-sm font-bold text-indigo-700 bg-transparent border-0 p-0 cursor-pointer hover:underline"
      data-testid="element-back">
      &larr; Back to Insights
    </button>
  );

  if (status === 'loading') {
    return (
      <div className="flex flex-col gap-4">
        {back}
        <div className={CARD} role="status" aria-label="Loading">
          <div className="h-6 bg-slate-100 rounded w-1/3" />
          <div className="h-4 bg-slate-100 rounded w-1/2 mt-3" />
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="flex flex-col gap-4">
        {back}
        <div className={CARD}>
          <p className="text-slate-600 m-0 mb-4">We couldn&rsquo;t load this {element.kind}.</p>
          <button
            type="button"
            onClick={load}
            className="px-4 py-2 rounded-xl bg-slate-900 text-white border-0 cursor-pointer">
            Try again
          </button>
        </div>
      </div>
    );
  }

  const { element: item, count, notUsed, average, distribution, ratings } = data;
  const most = Math.max(1, ...distribution);
  const shown = star === null ? ratings : ratings.filter(r => r.value === star);
  const detail = [item.place, item.service].filter(Boolean).join(', ');

  return (
    <div className="flex flex-col gap-4" data-testid="element-detail">
      {back}

      <section className={CARD}>
        <div className="text-xs font-bold text-slate-500">
          {KIND[item.kind]}
          {element.scope ? ` · ${element.scope}` : ''}
        </div>
        <h2 className="text-xl font-black text-slate-900 m-0 mt-1">{item.name}</h2>
        {detail && <p className="text-sm text-slate-500 m-0 mt-1">{detail}</p>}

        <div className="grid grid-cols-1 md:grid-cols-[16rem_minmax(0,1fr)] gap-6 mt-5">
          <div>
            <div className="flex items-baseline gap-2">
              <span
                className="text-4xl font-black text-slate-900 tabular-nums"
                data-testid="element-average">
                {average === null ? '-' : average.toFixed(1)}
              </span>
              <Stars value={average} size="text-xl" />
            </div>
            <p className="text-sm text-slate-600 m-0 mt-2" data-testid="element-count">
              {count} {count === 1 ? 'rating' : 'ratings'}
              {notUsed > 0 ? ` · ${notUsed} ${NOT_USED[item.kind].toLowerCase()}` : ''}
            </p>
          </div>

          <div className="flex flex-col gap-1.5" role="group" aria-label="Filter by stars">
            {[5, 4, 3, 2, 1].map(s => (
              <button
                key={s}
                type="button"
                onClick={() => setStar(star === s ? null : s)}
                aria-pressed={star === s}
                data-testid={`element-star-${s}`}
                className={`flex items-center gap-3 rounded-lg px-2 py-1 border-0 cursor-pointer text-left ${
                  star === s ? 'bg-indigo-50' : 'bg-transparent hover:bg-slate-50'
                }`}>
                <span className="w-14 text-xs font-bold text-slate-600">
                  {s} {s === 1 ? 'star' : 'stars'}
                </span>
                <span
                  className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden"
                  aria-hidden="true">
                  <span
                    className="block h-full rounded-full bg-amber-400"
                    style={{ width: `${(distribution[s - 1] / most) * 100}%` }}
                  />
                </span>
                <span className="w-8 text-right text-xs font-bold text-slate-700 tabular-nums">
                  {distribution[s - 1]}
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className={CARD}>
        <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
          <h3 className="text-base font-black text-slate-900 m-0">
            {star === null
              ? 'Every guest'
              : `Guests who gave ${star} ${star === 1 ? 'star' : 'stars'}`}
          </h3>
          {star !== null && (
            <button
              type="button"
              onClick={() => setStar(null)}
              className="text-sm font-bold text-slate-500 bg-transparent border-0 cursor-pointer hover:text-slate-900">
              Show everyone
            </button>
          )}
        </div>
        {shown.length === 0 ? (
          <p className="text-sm text-slate-500 m-0" data-testid="element-empty">
            No guest has rated this yet.
          </p>
        ) : (
          <ul className="list-none m-0 p-0 flex flex-col divide-y divide-slate-100">
            {shown.map(r => (
              <li key={r.responseId}>
                <button
                  type="button"
                  onClick={() => openResponse(r.responseId)}
                  disabled={opening === r.responseId}
                  className="w-full text-left bg-transparent border-0 py-3 px-1 cursor-pointer hover:bg-slate-50 rounded-lg grid grid-cols-1 md:grid-cols-[8rem_minmax(0,14rem)_minmax(0,1fr)] gap-x-4 gap-y-1 items-start"
                  data-testid={`element-rating-${r.responseId}`}>
                  <span className="flex flex-col gap-1">
                    {r.value === null ? (
                      <span className="text-xs font-bold text-slate-500">
                        {NOT_USED[item.kind]}
                      </span>
                    ) : (
                      <>
                        <Stars value={r.value} size="text-sm" />
                        <span className="text-xs font-bold text-slate-700">
                          {r.value} · {ratingWord(r.value)}
                        </span>
                      </>
                    )}
                  </span>
                  <span className="text-xs text-slate-500 min-w-0">
                    <span className="block text-sm font-bold text-slate-900 truncate">
                      {r.tourName}
                    </span>
                    Departed {formatDeparture(r.departureDate)}
                    {r.guideName ? `, ${r.guideName}` : ''}
                    <span className="block">
                      Sent {formatSubmitted(r.submittedAt)} · overall {r.overallScore}/5
                    </span>
                  </span>
                  <span className="text-sm text-slate-800 min-w-0">
                    {r.comment ? (
                      `“${r.comment}”`
                    ) : (
                      <span className="text-slate-400">No comment</span>
                    )}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {open && (
        <ResponseDrawer
          response={open}
          onClose={() => setOpen(null)}
          onRemoved={() => {
            setOpen(null);
            load();
          }}
        />
      )}
    </div>
  );
};

ElementDetail.propTypes = {
  element: PropTypes.shape({
    kind: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    place: PropTypes.string,
    tourId: PropTypes.string,
    from: PropTypes.string,
    scope: PropTypes.string
  }).isRequired,
  onBack: PropTypes.func.isRequired
};

export default ElementDetail;
